<div class="dashboard">
  <!-- Header card with KPIs -->
  <mat-card class="header-card">
    <div class="header-top">
      <div class="title">
        <mat-icon aria-hidden="true">space_dashboard</mat-icon>
        <h2>Dashboard</h2>
        <span class="chip info">Last 6 orders</span>
      </div>
    </div>
    <p class="subtitle">Overview of your ads performance and quick actions</p>

    <div class="kpi-grid in-header">
      <div class="kpi">
        <div class="kpi-label">Total Impressions</div>
        @if (loadingOrders) {
          <div class="skeleton-kpi"></div>
        } @else {
          <div class="kpi-value">{{ compactNumber(kpiTotalImpressions) }}</div>
        }
      </div>
      <div class="kpi">
        <div class="kpi-label">Total Cost</div>
        @if (loadingOrders) {
          <div class="skeleton-kpi"></div>
        } @else {
          <div class="kpi-value">{{ kpiTotalCost | number:'1.0-2' }}<span class="kpi-suffix"> USD</span></div>
        }
      </div>
      <div class="kpi">
        <div class="kpi-label">Total Airings</div>
        @if (loadingOrders) {
          <div class="skeleton-kpi"></div>
        } @else {
          <div class="kpi-value">{{ kpiTotalAirings }}</div>
        }
      </div>
    </div>
    <div class="kpi-note">Metrics reflect activity in the last 6 orders.</div>
  </mat-card>

  <app-schedule-day-lockout></app-schedule-day-lockout>

  @if (isAdvertiserUserOnly) {
    <mat-card class="card offers-widget" routerLink="/my-offers">
      <div class="offers-widget-inner">
        <div class="offers-widget-main">
          <div class="offers-widget-title">
            <mat-icon aria-hidden="true">local_offer</mat-icon>
            <h3>My Offers</h3>
          </div>
          @if (loadingOffers) {
            <p class="offers-count loading">Loading…</p>
          } @else if (offersCount === 0) {
            <p class="offers-count">No offers right now</p>
          } @else {
            <p class="offers-count">{{ offersCount }} {{ offersCount === 1 ? 'offer' : 'offers' }} available</p>
          }
        </div>
        <mat-icon class="offers-chevron" aria-hidden="true">chevron_right</mat-icon>
      </div>
    </mat-card>
  }

  <div class="sections">
    <!-- Recent Orders -->
    <mat-card class="card full-width surface-card">
      <div class="card-head">
        <div class="card-title">
          <mat-icon aria-hidden="true">receipt_long</mat-icon>
          <h2>Recent Orders</h2>
          <span class="chip info">Last 6 orders</span>
        </div>
        <button type="button" class="btn-primary" routerLink="/campaignbuilder">
          <mat-icon aria-hidden="true">add</mat-icon>
          Create Order
        </button>
      </div>

      @if (loadingOrders) {
        <div class="orders-skeleton">
          @for (i of [1,2,3]; track i) {
            <div class="skeleton-order-card">
              <div class="skeleton-header"></div>
              <div class="skeleton-text"></div>
              <div class="skeleton-body">
                <div class="skeleton-metrics">
                  <div class="skeleton-metric"></div>
                  <div class="skeleton-metric"></div>
                  <div class="skeleton-metric"></div>
                </div>
                <div class="skeleton-logo"></div>
              </div>
            </div>
          }
        </div>
      } @else {
        <div class="orders-grid">
          @if (recentOrders.length === 0) {
            <div class="empty">
              <mat-icon aria-hidden="true">playlist_remove</mat-icon>
              @if (role === 'user' && orderEligibility) {
                @if (orderEligibility.hasApprovedRecording) {
                  <p>No orders yet. Create an order to air your commercial.</p>
                  <button type="button" class="btn-primary" routerLink="/campaignbuilder">
                    <mat-icon aria-hidden="true">add</mat-icon>
                    Create Order
                  </button>
                } @else if (orderEligibility.hasRecordings) {
                  <p>Wait for the recordings to get approved then you can create the order.</p>
                } @else {
                  <p>First create a commercial and wait for the approval once it is approved you can create the order.</p>
                  <button type="button" class="btn-primary" (click)="goToCreate()">
                    <mat-icon aria-hidden="true">mic</mat-icon>
                    Create Recording
                  </button>
                }
              } @else {
                <p>No orders yet.</p>
              }
            </div>
          } @else {
            <div class="orders-grid-inner">
              @for (o of recentOrders; track o.id) {
                <div class="order-card clickable" (click)="openOrderSchedules(o.id)">
                  <div class="order-header-row">
                    <div class="order-header-main">
                      <div class="order-id-heading">{{ orderStationLabel(o) }}</div>
                      <div class="station-chip">Order #{{ o.id }}</div>
                    </div>
                    <button
                      *ngIf="isRegularUser || isStaffRole"
                      class="btn-secondary btn-sm order-download-btn"
                      type="button"
                      (click)="downloadReceipt(o.id); $event.stopPropagation()"
                      [disabled]="downloadingOrderId === o.id || isDownloadingReceipt"
                      [attr.aria-busy]="downloadingOrderId === o.id"
                      title="Download Receipt">
                      @if (downloadingOrderId === o.id) {
                        <span class="btn-spinner-dark" aria-hidden="true"></span>
                      } @else {
                        <mat-icon aria-hidden="true">download</mat-icon>
                      }
                      <span>Download</span>
                    </button>
                  </div>

                  <div class="order-time-range">{{ o.startdate }} → {{ o.enddate }}</div>
                  @if (showOrderPlacedBy && orderCustomer(o); as customer) {
                    <div class="order-placed-by">
                      <mat-icon aria-hidden="true">person</mat-icon>
                      <span><strong>{{ customer.label }}:</strong> {{ customer.value }}</span>
                    </div>
                  }

                  <div class="order-body-row">
                    <div class="order-metrics-col">
                      <div class="metric-item impressions">
                        <mat-icon aria-hidden="true">insights</mat-icon>
                        <span class="metric-value">{{ compactNumber(o.totalimpressions) }}</span>
                        <span class="metric-label">impressions</span>
                      </div>
                      <div class="metric-item airings">
                        <mat-icon aria-hidden="true">broadcast_on_home</mat-icon>
                        <span class="metric-value">{{ o.totalairings }}</span>
                        <span class="metric-label">airings</span>
                      </div>
                      <div class="metric-item cost" [class.has-promo]="orderPricing(o).hasPromo">
                        <mat-icon aria-hidden="true">attach_money</mat-icon>
                        @if (orderPricing(o).hasPromo) {
                          <div class="cost-promo-stack">
                            <span class="cost-original">${{ orderPricing(o).originalAmount | number:'1.2-2' }}</span>
                            <span class="cost-paid">${{ orderPricing(o).paidAmount | number:'1.2-2' }}</span>
                            @if (orderPricing(o).promoCode) {
                              <span class="cost-promo-line">
                                Promo: {{ orderPricing(o).promoCode }} saved you ${{ orderPricing(o).discountAmount | number:'1.2-2' }}
                              </span>
                            }
                          </div>
                          <span class="metric-label">USD paid</span>
                        } @else {
                          <span class="metric-value">${{ orderPricing(o).paidAmount | number:'1.2-2' }}</span>
                          <span class="metric-label">USD</span>
                        }
                      </div>
                    </div>

                    <div class="order-logo-col">
                      <div class="station-logo-container" *ngIf="o.logo_url">
                        <img [src]="o.logo_url" class="order-station-logo" alt="Station Logo">
                      </div>
                      <div class="station-avatar" *ngIf="!o.logo_url">
                        {{ (o.station_name || 'S').charAt(0) }}
                      </div>
                    </div>
                  </div>
                </div>
              }
            </div>
          }
        </div>
        <div class="see-all center">
          <button type="button" class="see-full-link" (click)="goToOrdersList()">See full list</button>
        </div>
      }
    </mat-card>

    <!-- Recordings + CTAs -->
    <div class="actions-row two-col">
      <div class="actions-col left">
        <ng-container [ngTemplateOutlet]="recordingsBlock"></ng-container>
      </div>
      <div class="actions-col right">
        <div class="cta-stack">
          <mat-card class="card cta primary-cta surface-card">
            <div class="cta-content">
              <div class="cta-text">
                <h3>Create New Recording</h3>
                <p>Start a fresh audio recording for your next campaign.</p>
              </div>
              <button type="button" class="btn-primary" (click)="goToCreate()" aria-label="Create new recording">
                <mat-icon aria-hidden="true">mic</mat-icon>
                Create Recording
              </button>
            </div>
          </mat-card>

          <mat-card class="card cta accent secondary-cta surface-card">
            <div class="cta-content">
              <div class="cta-text">
                <h3>Let us Create a Commercial For You</h3>
                <p>Provide the details and we’ll have a commercial produced for you.</p>
              </div>
              <button type="button" class="btn-primary" (click)="goToCommercial()" aria-label="Request a produced commercial">
                <mat-icon aria-hidden="true">campaign</mat-icon>
                Commercial produced for me
              </button>
            </div>
          </mat-card>
        </div>
      </div>
    </div>

    <ng-template #recordingsBlock>
      <mat-card class="card recordings-card surface-card">
        <div class="card-head">
          <div class="card-title">
            <mat-icon aria-hidden="true">library_music</mat-icon>
            <h2>{{ role === 'user' ? 'Recent Recordings' : 'Pending Approvals' }}</h2>
            <span class="chip info">{{ role === 'user' ? 'Last 3' : 'Latest 3' }}</span>
          </div>
        </div>

        @if (loadingRecordings) {
          <div class="recordings-skeleton">
            @for (i of [1,2,3]; track i) {
              <div class="skeleton-rec-card">
                <div class="skeleton-rec-header"></div>
                <div class="skeleton-rec-audio"></div>
              </div>
            }
          </div>
        } @else if (!hasRecordings) {
          <div class="empty">
            <mat-icon aria-hidden="true">inbox</mat-icon>
            <p>No recordings yet. Create your first one below.</p>
          </div>
        } @else {
          <div class="recordings">
            @for (r of recentRecordings; track r.id) {
              <div class="rec-item-card">
                <div class="rec-item">
                  <div class="rec-main">
                    <div class="rec-name-row">
                      <div class="rec-name truncate">
                        {{ r.name | titlecase }}
                        <span class="station-name-sub" *ngIf="r.station_name">({{ r.station_name }})</span>
                      </div>
                      <div class="rec-status-area">
                        <span class="chip" [ngClass]="statusClass[r.approval_status]">
                          {{ customStatusLabels[r.approval_status] }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="rec-audio">
                    <audio controls preload="metadata" (loadedmetadata)="onAudioMetadata(r, $event)">
                      <source [src]="r.presigned_url || r.audio_url || r.cloudfront_url" type="audio/mpeg" />
                      Your browser does not support the audio element.
                    </audio>
                  </div>
                  <div class="rec-actions" *hasRole="['admin', 'station_owner', 'station_user', 'owner']">
                    @if (canModerateRecording(r)) {
                      <button type="button" class="btn-primary btn-sm" (click)="approveRecording(r)">
                        <mat-icon aria-hidden="true">check</mat-icon>
                        Approve
                      </button>
                      <button type="button" class="btn-danger-outline btn-sm" (click)="declineRecording(r)">
                        <mat-icon aria-hidden="true">block</mat-icon>
                        Decline
                      </button>
                    }
                  </div>
                </div>
              </div>
            }
          </div>
          <div class="see-all center">
            <button type="button" class="see-full-link" (click)="goToRecordingsList()">See full list</button>
          </div>
        }
      </mat-card>
    </ng-template>
  </div>
</div>
